import { FormEvent, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { loginSchema } from '@fellowship/shared'
import { useLogin } from '../hooks/useAuth'
import { ApiError } from '../lib/api'
import { Wordmark } from '../components/ui/Wordmark'
import { SocialAuthButtons } from '../components/auth/SocialAuthButtons'

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({})
  const [searchParams] = useSearchParams()
  const login = useLogin()

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setFieldErrors({})
    const parsed = loginSchema.safeParse({ email, password })
    if (!parsed.success) {
      const flat = parsed.error.flatten().fieldErrors
      setFieldErrors({ email: flat.email?.[0], password: flat.password?.[0] })
      return
    }
    login.mutate(parsed.data)
  }

  const serverError = login.error instanceof ApiError ? login.error.message : null

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', fontFamily: '"Outfit", system-ui, sans-serif' }}>
      <div style={{ marginBottom: 28 }}>
        <Link to="/" style={{ textDecoration: 'none' }}>
          <Wordmark size={24} />
        </Link>
      </div>

      {/* Login card */}
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 24,
        padding: '40px 44px',
        width: '100%',
        maxWidth: 420,
        boxShadow: '0 1px 2px rgba(31,26,46,.04), 0 18px 40px rgba(31,26,46,.07)',
      }}>
        {/* Eyebrow */}
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.10em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 14 }}>
          WELCOME BACK
        </div>

        <h1 style={{ margin: '0 0 24px', fontSize: 32, fontWeight: 600, letterSpacing: '-0.025em', color: 'var(--ink)', lineHeight: 1.1 }}>
          Log in
        </h1>

        <SocialAuthButtons />

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
          <div style={{ flex: 1, height: 1, background: 'var(--line-soft)' }} />
          <span style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 500 }}>or</span>
          <div style={{ flex: 1, height: 1, background: 'var(--line-soft)' }} />
        </div>

        {searchParams.get('reset') === '1' && (
          <div style={{ padding: '12px 16px', borderRadius: 12, marginBottom: 20, background: '#dcfce7', border: '1px solid #bbf7d0', color: '#15803d', fontSize: 13.5 }}>
            Password reset successfully. Please log in.
          </div>
        )}

        {serverError && (
          <div role="alert" style={{ padding: '12px 16px', borderRadius: 12, marginBottom: 20, background: '#fee2e2', border: '1px solid #fca5a5', color: '#b91c1c', fontSize: 13.5 }}>
            {serverError}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label htmlFor="email" style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-2)' }}>
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              aria-describedby={fieldErrors.email ? 'email-error' : undefined}
              aria-invalid={Boolean(fieldErrors.email)}
            />
            {fieldErrors.email && (
              <div id="email-error" style={{ fontSize: 12, color: '#b91c1c' }}>{fieldErrors.email}</div>
            )}
          </div>

          <div style={{ marginBottom: 28, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label htmlFor="password" style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-2)' }}>
                Password
              </label>
              <Link to="/forgot-password" style={{ fontSize: 12.5, color: 'var(--muted)', textDecoration: 'none', fontWeight: 500 }}>
                Forgot password?
              </Link>
            </div>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              aria-describedby={fieldErrors.password ? 'password-error' : undefined}
              aria-invalid={Boolean(fieldErrors.password)}
            />
            {fieldErrors.password && (
              <div id="password-error" style={{ fontSize: 12, color: '#b91c1c' }}>{fieldErrors.password}</div>
            )}
          </div>

          <button
            type="submit"
            disabled={login.isPending}
            className="btn btn--full btn--lg"
          >
            {login.isPending ? 'Logging in…' : 'Log in'}
          </button>
        </form>

        <p style={{ marginTop: 20, textAlign: 'center', fontSize: 13.5, color: 'var(--muted)' }}>
          Don&apos;t have an account?{' '}
          <Link to="/register" style={{ color: 'var(--primary)', fontWeight: 600, textDecoration: 'none' }}>
            Sign up
          </Link>
        </p>

        <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--line-soft)', textAlign: 'center', fontSize: 12.5, color: 'var(--muted)' }}>
          Running a church?{' '}
          <Link to="/host" style={{ color: 'var(--primary)', fontWeight: 600, textDecoration: 'none' }}>
            Host events on Fellowship →
          </Link>
        </div>
      </div>
    </div>
  )
}
